'use client';

import { PortalLink, tx, useLocale } from '@akademi/site-builder-runtime';
import { useSolarText } from '../locales/useSolarText';
import { formatMinor, type LineTotal } from './money';
import type { AddState } from './useLiveProducts';

type Localized = string | Record<string, string>;

export interface LinkAction {
  label: Localized;
  href: string;
}

/**
 * A live price: the published figure large, the other basis small beneath it.
 *
 * <p>While the lines load it holds the space with a placeholder of the same height, so the
 * slide does not jump when the price lands. When the price cannot be shown — a slug that no
 * longer resolves, a product with no price — it renders nothing: an offer with no number is
 * still an offer, a "£NaN" is a broken shop.
 */
export function LivePrice({
  total,
  loading,
  note,
  size = 'md',
}: {
  total: LineTotal | null;
  loading: boolean;
  note?: Localized;
  size?: 'md' | 'lg';
}) {
  const t = useSolarText();
  const { locale } = useLocale();
  const cls = `ae-price ae-price--${size}`;

  if (loading) return <div className={`${cls} ae-price--loading`} aria-hidden="true" />;
  if (!total) return null;

  const basis = (inclusive: boolean) => t(inclusive ? 'home.incVat' : 'home.exVat');
  return (
    <div className={cls}>
      <span className="ae-price__now">{formatMinor(total.published, total.currency, locale)}</span>
      <span className="ae-price__basis">
        {basis(total.publishedInclusive)}
        {tx(note) ? ` · ${tx(note)}` : ''}
      </span>
      {total.alternate !== undefined ? (
        <span className="ae-price__alt">
          {formatMinor(total.alternate, total.currency, locale)} {basis(!total.publishedInclusive)}
        </span>
      ) : null}
    </div>
  );
}

/** The add button and what happened, with a way to the basket once it worked. */
export function AddButton({
  label,
  state,
  disabled,
  outOfStock,
  onAdd,
  basketHref = '/cart',
  tone = 'accent',
}: {
  label: Localized;
  state: AddState;
  disabled: boolean;
  outOfStock?: boolean;
  onAdd: () => void;
  basketHref?: string;
  tone?: 'accent' | 'brand';
}) {
  const t = useSolarText();
  const text =
    state === 'busy' ? t('home.adding') : outOfStock ? t('home.outOfStock') : tx(label) || t('home.addToBasket');

  return (
    <>
      <button
        type="button"
        className={`ae-btn ae-btn--${tone}`}
        disabled={disabled || outOfStock || state === 'busy'}
        onClick={onAdd}
      >
        {text}
      </button>
      <span className="ae-add-status" role="status" aria-live="polite">
        {state === 'added' ? (
          <>
            {t('home.added')} <PortalLink href={basketHref}>{t('home.viewBasket')}</PortalLink>
          </>
        ) : state === 'failed' ? (
          t('home.addFailed')
        ) : null}
      </span>
    </>
  );
}

export function ActionLink({ action, tone }: { action?: LinkAction; tone: 'accent' | 'brand' | 'ghost' }) {
  if (!action?.href || !tx(action.label)) return null;
  return (
    <PortalLink href={action.href} className={`ae-btn ae-btn--${tone}`}>
      {tx(action.label)}
    </PortalLink>
  );
}
